/**
 * The reading panel's pages and its scrolling by keys (ADR-077 decisions 3 and 4), as arithmetic over heights the
 * panel measures: a page is as many whole lines as fit the text box, the text takes as many pages as its lines need,
 * and Page Down, Page Up, Home and End turn pages or scroll a text that scrolls by the box's whole lines. Heights are in
 * any one unit (the page measures device pixels).
 */

/**
 * The keys that turn a page or scroll, in the order the panel lists them.
 */
export const PAGE_KEYS = ["PageDown", "PageUp", "Home", "End"] as const;

/**
 * How a text breaks into pages: the whole lines a page holds, the number of pages, and the line height they were
 * counted in.
 */
export interface PageGeometry {
  readonly lines: number;
  readonly count: number;
  readonly lineHeight: number;
}

/**
 * Slack for heights measured in fractional device pixels, so a box exactly ten lines tall counts ten.
 */
const SLACK = 1e-6;

/**
 * The pages a text takes in a box: the whole lines that fit the box, at least one, and the text's lines over that,
 * rounded up, at least one page.
 *
 * @param boxHeight The height the text box has for lines.
 * @param contentHeight The text's whole height.
 * @param lineHeight One line's height.
 * @returns The geometry.
 * @example
 * pageGeometry(115, 341, 11); // { lines: 10, count: 4, lineHeight: 11 }
 */
export function pageGeometry(boxHeight: number, contentHeight: number, lineHeight: number): PageGeometry {
  const lines = Math.max(1, Math.floor(boxHeight / lineHeight + SLACK));
  const textLines = Math.max(1, Math.round(contentHeight / lineHeight));
  return { lines, count: Math.ceil(textLines / lines), lineHeight };
}

/**
 * Where a page starts in the text.
 *
 * @param page The page, from 0.
 * @param geometry The text's pages.
 * @returns The offset of its first line.
 */
export function pageTop(page: number, geometry: PageGeometry): number {
  return page * geometry.lines * geometry.lineHeight;
}

/**
 * The page a key turns to: the next or the one before for Page Down and Page Up, the first or the last for Home and
 * End, never past either end; any other key keeps the page.
 *
 * @param key The `KeyboardEvent.key` value.
 * @param page The page shown, from 0.
 * @param count The number of pages.
 * @returns The page to show.
 */
export function turnPage(key: string, page: number, count: number): number {
  const last = Math.max(0, count - 1);
  switch (key) {
    case "PageDown":
      return Math.min(last, page + 1);
    case "PageUp":
      return Math.max(0, page - 1);
    case "Home":
      return 0;
    case "End":
      return last;
    default:
      return page;
  }
}

/**
 * Where a key scrolls a text that scrolls: down or up by the box's whole lines (one line at least) for Page Down and
 * Page Up, to the top for Home and to the end for End, never past either; any other key keeps the place.
 *
 * @param key The `KeyboardEvent.key` value.
 * @param text The text's scroll offset, its whole height, its box's height and its line height.
 * @returns The scroll offset to take.
 */
export function scrollFor(key: string, text: { readonly scrollTop: number; readonly scrollHeight: number; readonly clientHeight: number; readonly lineHeight: number }): number {
  const end = Math.max(0, text.scrollHeight - text.clientHeight);
  const step = Math.max(1, Math.floor(text.clientHeight / text.lineHeight + SLACK)) * text.lineHeight;
  switch (key) {
    case "PageDown":
      return Math.min(end, text.scrollTop + step);
    case "PageUp":
      return Math.max(0, text.scrollTop - step);
    case "Home":
      return 0;
    case "End":
      return end;
    default:
      return text.scrollTop;
  }
}
